/**
 * Which referral put a EUR 100 credit on which of these rent rows (COL-434).
 *
 * One RPC for the whole rent table, keyed by payment id, so the row component
 * can render "Successfully referred X to room Y" without a query per row. The
 * fact itself lives on referrals.reward_payment_id — nothing is stored on the
 * payment — so this is the only place the admin table learns why a month is
 * EUR 100 short.
 *
 * Plain effect + state rather than react-query on purpose: PaymentsTab is
 * rendered without a QueryClientProvider in its own tests (DepositCard,
 * RentSchedule, SepaCard), and a hook that needs the provider would make the
 * tab un-renderable there. A failed lookup degrades to "no notes" — the rent
 * table must never break because the explanatory line could not load.
 *
 * Staff only: the RPC refuses non-staff, and the names it returns are unmasked.
 *
 * Takes `id:amount_cents` tokens, not bare ids: a reward can land on a rent
 * row that was already fetched (a referral reaching 'signed' while an
 * existing rent row already satisfies it), which changes that row's
 * amount_cents without adding or removing any payment id. Keying only on ids
 * would never re-fetch for that case, leaving the row's rent visibly reduced
 * with no explanatory note and no "Referral credit" chip until the tab
 * remounts.
 */
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  groupReferralCreditNotes,
  type ReferralCreditNote,
} from "@/lib/referrals";

export function useReferralCreditNotes(
  paymentTokens: readonly string[]
): Record<string, ReferralCreditNote[]> {
  // A sorted, de-duplicated key so the lookup does not rerun when the caller
  // re-creates the same array on every render, but does rerun when any
  // token's amount_cents suffix changes.
  const key = Array.from(new Set(paymentTokens.filter(Boolean)))
    .sort()
    .join(",");
  const [notes, setNotes] = useState<ReferralCreditNote[]>([]);

  useEffect(() => {
    if (!key) {
      setNotes([]);
      return undefined;
    }
    const ids = Array.from(
      new Set(key.split(",").map((token) => token.split(":")[0]))
    );
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc(
          "admin_referral_credit_notes",
          { _payment_ids: ids }
        );
        if (error) throw new Error(error.message);
        // jsonb: an array of ReferralCreditNote, or null.
        const rows = data as unknown as ReferralCreditNote[] | null;
        if (!cancelled) setNotes(rows ?? []);
      } catch {
        if (!cancelled) setNotes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  return useMemo(() => groupReferralCreditNotes(notes), [notes]);
}
